import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import ActionButton from 'components/ApplicantDrawerDetailsList/components/Field/ActionButtons/ActionButton';
import {
  DATA,
  DETAIL_TYPES,
  SECURE_DATA,
  STRUCTURED_DATA,
} from 'components/ApplicantDrawerDetailsList/constants';
import { serializeEmploymentHistory } from 'components/ApplicantEmploymentHistoryContainer/serialize';
import {
  addDefaultErrorMessageAction,
  addMessageAction,
} from 'containers/FlashMessage/actions';
import useUserPermissions from 'hooks/useUserPermissions';
import messages from 'shared/global/messages';

import { DetailType, DetailValueType } from '../../../props';
import { ActionTypeEnum } from './constants';

const ActionButtonsContainer = ({
  detail,
  type,
  isFieldEmpty,
  onClickCopy = () => {},
  onClickClear = () => {},
  onClickEdit = () => {},
  value,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { formatMessage } = intl;
  const { manageApplicantsPermission, manageSecureDataPermission } =
    useUserPermissions();

  const canManageApplicants =
    manageApplicantsPermission && type !== SECURE_DATA;
  const canManageSecureData = manageSecureDataPermission;
  // User can edit/delete field if one of:
  // 1. Field is -not- secure, and user has manage_applicants permission
  // 2. User has manage_secure_data permission
  const canManageField = canManageApplicants || canManageSecureData;

  const copySuccess = formatMessage(messages.copySuccess);
  const tooltipCopy = formatMessage(messages.copy);
  const tooltipDelete = formatMessage(messages.clear);
  const tooltipEdit = formatMessage(messages.edit);

  const handleClickCopy = event => {
    event.stopPropagation();

    let text = value;
    if (detail.type === 'address') {
      const filteredAddressParts = [
        value.street_name,
        value.address_2,
        value.city,
        value.state,
        value.zipcode,
        value.country,
      ].filter(
        addressValue => typeof addressValue === 'string' && addressValue,
      );

      text =
        filteredAddressParts.length > 0
          ? filteredAddressParts.reduce((a, b) => `${a}, ${b}`)
          : '';
    } else if (detail.type === DETAIL_TYPES.history_employment) {
      text = serializeEmploymentHistory(value, intl);
    }

    const handleSuccess = () =>
      dispatch(addMessageAction(copySuccess, 'success'));

    const handleError = () => dispatch(addDefaultErrorMessageAction());

    window.navigator.clipboard.writeText(text).then(handleSuccess, handleError);
    onClickCopy();
  };

  const handleClickClear = event => {
    event.stopPropagation();
    onClickClear();
  };

  const handleClickEdit = event => {
    event.stopPropagation();
    onClickEdit();
  };

  return (
    <>
      {canManageField && (
        <ActionButton
          actionType={ActionTypeEnum.EDIT}
          onClick={handleClickEdit}
          tooltipTitle={tooltipEdit}
        />
      )}
      {!isFieldEmpty && (
        <ActionButton
          actionType={ActionTypeEnum.COPY}
          onClick={handleClickCopy}
          tooltipTitle={tooltipCopy}
        />
      )}
      {canManageField && (
        <ActionButton
          actionType={ActionTypeEnum.DELETE}
          onClick={handleClickClear}
          tooltipTitle={tooltipDelete}
        />
      )}
    </>
  );
};

ActionButtonsContainer.propTypes = {
  detail: DetailType.isRequired,
  type: PropTypes.oneOf([DATA, SECURE_DATA, STRUCTURED_DATA]),
  isFieldEmpty: PropTypes.bool.isRequired,
  onClickCopy: PropTypes.func,
  onClickClear: PropTypes.func,
  onClickEdit: PropTypes.func,
  value: DetailValueType,
};

export default ActionButtonsContainer;
